import { defineDirective, FieldViewModel, ISoilAnalysis, soilColors, SsmClassification } from "../../omnia"
import { validateSsmValue } from "../../ssm/attrs"
import { getSsmGaugeStyle } from "../../ssm/colors"
import { SsmAttribute } from "../../ssm/types"
import { AttributeComment, EditorAttribute } from "./types"

export interface ISoilAllocTarget {
    field: FieldViewModel
    zoneId?: number
}

export class SsmAttributeCtrl {

    static create(): ng.IDirective {
        return defineDirective(SsmAttributeCtrl, "app/soil/edit/ssm/attr.html", {
            analysis: "<",
            attr: "<",
            showErrors: "<"
        })
    }


    analysis: ISoilAnalysis
    attr: EditorAttribute
    showErrors: boolean

    commentProp: AttributeComment

    classes: SsmClassification[]

    hasFocus: boolean

    private _gaugeStyle: {}
    private _defaultComment: string

    $onInit() {

        const { attr } = this

        this.commentProp = attr.comment

        this.updateValueDependencies()
        this.revalidate()

    }

    get value() {
        const { analysis, attr } = this
        const value = (analysis && attr) ? analysis[attr.property] : undefined
        return value && attr?.factor
            ? Math.round((value * attr.factor) * 10) / 10
            : value
    }

    set value(value: number) {
        const { analysis, attr } = this
        analysis[attr.property] = attr.factor
            ? Math.round((value / attr.factor) * 1000) / 1000
            : value
        this.revalidate()
        this.updateValueDependencies()
    }

    get step() {
        return this.attr?.inputStep ?? 1
    }

    get showGauge() {
        const { attr } = this
        return attr && (attr.hasClassification || attr.gaugeColorKey)
    }

    get gaugeStyle() {
        return this._gaugeStyle
    }

    get comment() {
        return this.commentProp?.text
    }

    set comment(text) {
        this.commentProp.text = text
        this.commentProp.useDefault = (text == this._defaultComment)
    }

    get usesDefaultComment() {
        return this.commentProp?.useDefault ?? true
    }

    get defaultComment() {
        return this._defaultComment
    }

    get commentPlaceholder() {
        return this.usesDefaultComment
            ? this.defaultComment
            : ""
    }

    get error() {
        return this.attr?.error
    }

    get required() {
        return this.attr?.required
    }

    setDefaultComment() {
        this.commentProp.text = this._defaultComment
        this.commentProp.useDefault = true
    }

    updateOnBlur() {
        this.hasFocus = false
        this.commentProp.useDefault = this.comment == this._defaultComment
        this.revalidate()
    }

    private revalidate() {
        const { analysis, attr } = this
        const value = analysis[attr.property]
        attr.error = validateSsmValue(attr, value)
    }

    private updateValueDependencies() {

        const { attr, value } = this
        const cls = attr.getClass(value)

        this._defaultComment = cls?.DefaultComment
        this._gaugeStyle = getGaugeStyle(attr, value, cls)

        if (this.commentProp.useDefault) {
            this.commentProp.text = this._defaultComment
        }

    }

}

function getGaugeStyle(attr: SsmAttribute, value: number, cls: SsmClassification) {

    if (attr.gaugeColorKey) {
        return soilColors.getGaugeStyle(value, attr.gaugeColorKey);
    }

    return getSsmGaugeStyle(cls?.ClassId)

}
